import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authService } from '../../services/api';
import Logo from '../../components/common/Logo';
import toast from 'react-hot-toast';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sentMessage, setSentMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res: any = await authService.forgotPassword(email);
      // Backend returns the same generic message whether or not the email exists,
      // to avoid leaking which addresses are registered.
      const msg = res?.message
        || 'If an account with that email exists, a password reset link has been sent.';
      setSentMessage(msg);
      toast.success('Check your inbox');
    } catch (error: any) {
      const apiMsg = error?.response?.data?.error?.message
        || error?.message
        || 'Failed to send reset email';
      toast.error(apiMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" aria-label="Sportofolo home" className="inline-block mb-4">
            <Logo variant="app-icon" className="w-20 h-20 mx-auto" />
          </Link>
          <Logo variant="light" className="h-12 sm:h-14 w-auto mx-auto" />
          <p className="text-gray-600 mt-3 tracking-wider text-xs sm:text-sm">PLAY. CONNECT. COMPETE.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Reset your password</h1>
          <p className="text-sm text-gray-600 mb-6">
            Enter the email tied to your account and we&apos;ll send you a reset link.
          </p>

          {sentMessage ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 text-green-800 text-sm rounded-lg p-4">
                {sentMessage}
              </div>
              <p className="text-xs text-gray-500">
                The link expires in 1 hour. Didn&apos;t get an email? Check spam, or try a different address.
              </p>
              <button
                onClick={() => { setSentMessage(null); setEmail(''); }}
                className="w-full bg-gray-100 text-gray-800 py-3 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Try a different email
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-400">📧</span>
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="input-field pl-10"
                    required
                    autoFocus
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gray-900 text-white py-3 px-4 rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : 'Send reset link'}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900">
              ← Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
